import React, { useState, useEffect } from 'react';
import { 
  Facebook, RefreshCw, Settings, Shield, Check, AlertCircle, X, 
  Globe, Star, BarChart2, Clock, Calendar, Bell, Zap, Sliders
} from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/Button';

interface ReviewProvider {
  id: string;
  name: string;
  icon: React.ReactNode;
  connected: boolean;
  lastSync?: Date;
  accountName?: string;
  status: 'active' | 'pending' | 'error' | 'disconnected';
  reviewCount?: number;
  averageRating?: number;
}

interface ReviewSyncSettings {
  autoSync: boolean;
  syncFrequency: 'hourly' | 'daily' | 'weekly';
  notifyOnNewReviews: boolean;
  autoRespond: boolean;
  responseTemplates: {
    positive: string;
    neutral: string;
    negative: string;
  };
  privacySettings: {
    anonymizeCustomerData: boolean;
    storeReviewsLocally: boolean;
    limitDataRetention: boolean;
    retentionPeriodDays: number;
  };
}

const ReviewPlatformIntegration: React.FC = () => {
  const { user } = useAuth();
  const [providers, setProviders] = useState<ReviewProvider[]>([
    {
      id: 'facebook',
      name: 'Facebook',
      icon: <Facebook className="h-6 w-6 text-blue-600" />,
      connected: false,
      status: 'disconnected',
      reviewCount: 0,
      averageRating: 0
    },
    {
      id: 'google',
      name: 'Google My Business',
      icon: <Globe className="h-6 w-6 text-red-500" />,
      connected: false,
      status: 'disconnected',
      reviewCount: 0,
      averageRating: 0
    },
    {
      id: 'yahoo',
      name: 'Yahoo Local',
      icon: <Globe className="h-6 w-6 text-purple-600" />,
      connected: false,
      status: 'disconnected',
      reviewCount: 0,
      averageRating: 0
    }
  ]);

  const [syncSettings, setSyncSettings] = useState<ReviewSyncSettings>({
    autoSync: true,
    syncFrequency: 'daily',
    notifyOnNewReviews: true,
    autoRespond: false,
    responseTemplates: {
      positive: 'Thank you for your positive feedback! We appreciate your support and look forward to continuing to serve you.',
      neutral: 'Thank you for your feedback. We value your input and are always looking for ways to improve our service.',
      negative: 'We apologize for your experience. We take your feedback seriously and would like to address your concerns. Please contact our office directly so we can resolve this issue.'
    },
    privacySettings: {
      anonymizeCustomerData: true,
      storeReviewsLocally: true,
      limitDataRetention: false,
      retentionPeriodDays: 365
    }
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState<string | null>(null);
  const [showConfigModal, setShowConfigModal] = useState<string | null>(null);
  const [syncStats, setSyncStats] = useState({
    totalReviews: 0,
    averageRating: 0,
    successfulSyncs: 0,
    failedSyncs: 0,
    lastSuccessfulSync: null as Date | null
  });

  // Load saved credentials on component mount
  useEffect(() => {
    if (user) {
      loadReviewCredentials();
    }
  }, [user]);

  const loadReviewCredentials = async () => {
    if (!user) return;
    
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase
        .from('user_credentials')
        .select('provider, credentials')
        .eq('user_id', user.id)
        .in('provider', ['facebook_reviews', 'google_reviews', 'yahoo_reviews']);
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        // Update providers with connection status
        const updatedProviders = [...providers];
        let totalReviews = 0;
        let totalRating = 0;
        let ratingCount = 0;
        
        data.forEach(credential => {
          const providerKey = credential.provider.replace('_reviews', '');
          const providerIndex = updatedProviders.findIndex(p => p.id === providerKey);
          
          if (providerIndex !== -1) {
            const reviewCount = credential.credentials.reviewCount || 0;
            const averageRating = credential.credentials.averageRating || 0;
            
            updatedProviders[providerIndex] = {
              ...updatedProviders[providerIndex],
              connected: true,
              status: 'active',
              lastSync: new Date(credential.credentials.lastSync || Date.now()),
              accountName: credential.credentials.accountName || 'Business Account',
              reviewCount,
              averageRating
            };
            
            totalReviews += reviewCount;
            if (averageRating > 0) {
              totalRating += averageRating;
              ratingCount++;
            }
          }
        });
        
        setProviders(updatedProviders);
        
        // Update sync stats
        setSyncStats(prev => ({
          ...prev,
          totalReviews,
          averageRating: ratingCount > 0 ? totalRating / ratingCount : 0,
          lastSuccessfulSync: new Date()
        }));
      }
    } catch (error) {
      console.error('Error loading review credentials:', error);
      toast.error('Failed to load review platform connections');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async (providerId: string) => {
    if (!user) {
      toast.error('You must be logged in to connect review platforms');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // In a real implementation, this would initiate OAuth flow
      // For this demo, we'll simulate a successful connection
      
      // Simulate OAuth flow delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Generate mock review data
      const mockReviewCount = Math.floor(Math.random() * 50) + 10;
      const mockAverageRating = (Math.random() * 1.5) + 3.5; // Between 3.5 and 5.0
      
      // Store the credentials in the database
      const { error } = await supabase
        .from('user_credentials')
        .upsert({
          user_id: user.id,
          provider: `${providerId}_reviews`,
          credentials: {
            access_token: 'mock_access_token',
            refresh_token: 'mock_refresh_token',
            expiry: new Date(Date.now() + 3600000).toISOString(),
            accountName: `${providerId.charAt(0).toUpperCase() + providerId.slice(1)} Business`,
            lastSync: new Date().toISOString(),
            reviewCount: mockReviewCount,
            averageRating: parseFloat(mockAverageRating.toFixed(1))
          }
        });
      
      if (error) throw error;
      
      // Update the provider in state
      setProviders(prev => prev.map(provider => {
        if (provider.id === providerId) {
          return {
            ...provider,
            connected: true,
            status: 'active',
            lastSync: new Date(),
            accountName: `${providerId.charAt(0).toUpperCase() + providerId.slice(1)} Business`,
            reviewCount: mockReviewCount,
            averageRating: parseFloat(mockAverageRating.toFixed(1))
          };
        }
        return provider;
      }));
      
      // Update sync stats
      const connectedProviders = providers.filter(p => p.id !== providerId && p.connected);
      const totalReviews = connectedProviders.reduce((sum, p) => sum + (p.reviewCount || 0), 0) + mockReviewCount;
      
      let totalRating = connectedProviders.reduce((sum, p) => sum + (p.averageRating || 0), 0) + mockAverageRating;
      let ratingCount = connectedProviders.filter(p => p.averageRating && p.averageRating > 0).length + 1;
      
      setSyncStats(prev => ({
        ...prev,
        totalReviews,
        averageRating: ratingCount > 0 ? totalRating / ratingCount : 0,
        successfulSyncs: prev.successfulSyncs + 1,
        lastSuccessfulSync: new Date()
      }));
      
      toast.success(`Connected to ${providerId} successfully`);
    } catch (error) {
      console.error(`Failed to connect to ${providerId}:`, error);
      toast.error(`Failed to connect to ${providerId}`);
      
      // Update failed syncs count
      setSyncStats(prev => ({
        ...prev,
        failedSyncs: prev.failedSyncs + 1
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = async (providerId: string) => {
    if (!user) return;
    
    setIsLoading(true);
    
    try {
      // Delete the credentials from the database
      const { error } = await supabase
        .from('user_credentials')
        .delete()
        .eq('user_id', user.id)
        .eq('provider', `${providerId}_reviews`);
      
      if (error) throw error;
      
      // Get the provider being removed
      const providerToRemove = providers.find(p => p.id === providerId);
      
      // Update the provider in state
      setProviders(prev => prev.map(provider => {
        if (provider.id === providerId) {
          return {
            ...provider,
            connected: false,
            status: 'disconnected',
            lastSync: undefined,
            accountName: undefined,
            reviewCount: 0,
            averageRating: 0
          };
        }
        return provider;
      }));
      
      // Update sync stats
      if (providerToRemove && providerToRemove.connected) {
        const connectedProviders = providers.filter(p => p.id !== providerId && p.connected);
        const totalReviews = connectedProviders.reduce((sum, p) => sum + (p.reviewCount || 0), 0);
        
        let totalRating = connectedProviders.reduce((sum, p) => sum + (p.averageRating || 0), 0);
        let ratingCount = connectedProviders.filter(p => p.averageRating && p.averageRating > 0).length;
        
        setSyncStats(prev => ({
          ...prev,
          totalReviews,
          averageRating: ratingCount > 0 ? totalRating / ratingCount : 0
        }));
      }
      
      toast.success(`Disconnected from ${providerId}`);
    } catch (error) {
      console.error(`Failed to disconnect from ${providerId}:`, error);
      toast.error(`Failed to disconnect from ${providerId}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSync = async (providerId: string) => {
    try {
      const provider = providers.find(p => p.id === providerId);
      if (!provider?.connected) return;

      setIsSyncing(providerId);

      // Simulate API call to sync reviews
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Generate new random review count (slightly higher than before)
      const newReviewCount = (provider.reviewCount || 0) + Math.floor(Math.random() * 3) + 1;
      
      // Update provider with new review count
      setProviders(prev => prev.map(p => {
        if (p.id === providerId) {
          return { 
            ...p, 
            lastSync: new Date(),
            reviewCount: newReviewCount
          };
        }
        return p;
      }));
      
      // Update sync stats
      const connectedProviders = providers.filter(p => p.id !== providerId && p.connected);
      const totalReviews = connectedProviders.reduce((sum, p) => sum + (p.reviewCount || 0), 0) + newReviewCount;
      
      setSyncStats(prev => ({
        ...prev,
        totalReviews,
        successfulSyncs: prev.successfulSyncs + 1,
        lastSuccessfulSync: new Date()
      }));

      toast.success(`Successfully synced reviews from ${provider.name}`);
      setIsSyncing(null);
    } catch (error) {
      console.error('Sync error:', error);
      
      // Update failed syncs count
      setSyncStats(prev => ({
        ...prev,
        failedSyncs: prev.failedSyncs + 1
      }));
      
      toast.error(`Failed to sync reviews from ${providers.find(p => p.id === providerId)?.name}`);
      setIsSyncing(null);
    }
  };

  const handleSyncAll = async () => {
    try {
      setIsSyncing('all');

      // Sync each connected provider
      const connectedProviders = providers.filter(p => p.connected);
      
      for (const provider of connectedProviders) {
        // Simulate API call to sync reviews
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Generate new random review count (slightly higher than before)
        const newReviewCount = (provider.reviewCount || 0) + Math.floor(Math.random() * 3) + 1;
        
        // Update provider with new review count
        setProviders(prev => prev.map(p => {
          if (p.id === provider.id) {
            return { 
              ...p, 
              lastSync: new Date(),
              reviewCount: newReviewCount
            };
          }
          return p;
        }));
      }
      
      // Calculate new total reviews
      const totalReviews = providers
        .filter(p => p.connected)
        .reduce((sum, p) => sum + (p.reviewCount || 0), 0);
      
      // Update sync stats
      setSyncStats(prev => ({
        ...prev,
        totalReviews,
        successfulSyncs: prev.successfulSyncs + connectedProviders.length,
        lastSuccessfulSync: new Date()
      }));

      toast.success('All review platforms synced successfully');
    } catch (error) {
      console.error('Sync all error:', error);
      
      // Update failed syncs count
      setSyncStats(prev => ({
        ...prev,
        failedSyncs: prev.failedSyncs + 1
      }));
      
      toast.error('Failed to sync some review platforms');
    } finally {
      setIsSyncing(null);
    }
  };

  const handleSaveSettings = async () => {
    if (!user) return;
    
    setIsLoading(true);
    
    try {
      // In a real implementation, this would update settings in the database
      // For this demo, we'll just show a success message
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast.success('Review sync settings saved successfully');
    } catch (error) {
      console.error('Error saving review sync settings:', error);
      toast.error('Failed to save review sync settings');
    } finally {
      setIsLoading(false);
    }
  };

  const openConfigModal = (providerId: string) => {
    setShowConfigModal(providerId);
  };

  const closeConfigModal = () => {
    setShowConfigModal(null);
  };

  const getStatusBadge = (status: ReviewProvider['status']) => {
    switch (status) {
      case 'active':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <Check className="h-3 w-3 mr-1" />
          Active
        </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
          Pending
        </span>;
      case 'error':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertCircle className="h-3 w-3 mr-1" />
          Error
        </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <X className="h-3 w-3 mr-1" />
          Disconnected
        </span>;
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star 
            key={star} 
            className={`h-4 w-4 ${star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
          />
        ))}
        <span className="ml-1 text-sm font-medium">{rating.toFixed(1)}</span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Review Platform Integration</h2>
          <p className="mt-1 text-sm text-gray-500">
            Connect and sync customer reviews from multiple platforms
          </p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={syncSettings.syncFrequency}
            onChange={(e) => setSyncSettings(prev => ({
              ...prev,
              syncFrequency: e.target.value as 'hourly' | 'daily' | 'weekly'
            }))}
            className="rounded-md border-gray-300 text-sm focus:ring-primary focus:border-primary"
          >
            <option value="hourly">Hourly sync</option>
            <option value="daily">Daily sync</option>
            <option value="weekly">Weekly sync</option>
          </select>
          
          <Button
            onClick={handleSyncAll}
            className="py-1.5 px-3"
            disabled={isLoading || isSyncing !== null || !providers.some(p => p.connected)}
          >
            {isSyncing === 'all' ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Sync All
              </>
            )}
          </Button>
          
          <Button
            onClick={handleSaveSettings}
            className="py-1.5 px-3"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </div>
      </div>

      {/* Review Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Star className="h-5 w-5 text-primary" />
            </div>
          </div>
          <h3 className="text-2xl font-bold mt-4">{syncStats.totalReviews}</h3>
          <p className="text-gray-600">Total Reviews</p>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Star className="h-5 w-5 text-yellow-500" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            <h3 className="text-2xl font-bold">{syncStats.averageRating.toFixed(1)}</h3>
            <div className="ml-2">
              {renderStars(syncStats.averageRating)}
            </div>
          </div>
          <p className="text-gray-600">Average Rating</p>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-green-100 rounded-lg">
              <Check className="h-5 w-5 text-green-500" />
            </div>
          </div>
          <h3 className="text-2xl font-bold mt-4">{syncStats.successfulSyncs}</h3>
          <p className="text-gray-600">Successful Syncs</p>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Clock className="h-5 w-5 text-blue-500" />
            </div>
          </div>
          <h3 className="text-2xl font-bold mt-4">
            {syncStats.lastSuccessfulSync 
              ? syncStats.lastSuccessfulSync.toLocaleDateString() 
              : 'Never'}
          </h3>
          <p className="text-gray-600">Last Successful Sync</p>
        </div>
      </div>

      {/* Review Platforms */}
      <div className="grid grid-cols-1 gap-6">
        {providers.map((provider) => (
          <div
            key={provider.id}
            className={`bg-white rounded-lg border p-6 ${
              provider.connected ? 'border-primary' : 'border-gray-200'
            }`}
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                {provider.icon}
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-medium text-gray-900">{provider.name}</h3>
                    {provider.status && getStatusBadge(provider.status)}
                  </div>
                  {provider.connected && (
                    <div className="text-sm text-gray-500 mt-1">
                      <div>Account: {provider.accountName}</div>
                      {provider.lastSync && (
                        <div>Last synced: {provider.lastSync.toLocaleString()}</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {provider.connected && (
                <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8">
                  <div>
                    <div className="text-sm text-gray-500">Reviews</div>
                    <div className="font-semibold">{provider.reviewCount}</div>
                  </div>
                  
                  <div>
                    <div className="text-sm text-gray-500">Rating</div>
                    <div className="flex items-center">
                      {renderStars(provider.averageRating || 0)}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-2">
                {provider.connected ? (
                  <>
                    <button
                      onClick={() => handleSync(provider.id)}
                      className="p-2 text-gray-400 hover:text-gray-500"
                      title="Sync now"
                      disabled={isSyncing !== null}
                    >
                      {isSyncing === provider.id ? (
                        <RefreshCw className="h-5 w-5 animate-spin text-primary" />
                      ) : (
                        <RefreshCw className="h-5 w-5" />
                      )}
                    </button>
                    <button
                      onClick={() => openConfigModal(provider.id)}
                      className="btn btn-outline py-1.5 px-3"
                    >
                      Configure
                    </button>
                    <button
                      onClick={() => handleDisconnect(provider.id)}
                      className="btn btn-outline py-1.5 px-3"
                    >
                      Disconnect
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => handleConnect(provider.id)}
                    className="btn btn-primary py-1.5 px-3"
                    disabled={isLoading}
                  >
                    {isLoading && provider.id === 'facebook' ? 'Connecting...' : 'Connect'}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Global Review Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Review Sync Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Sync Preferences</h4>
            <div className="space-y-4">
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={syncSettings.autoSync}
                    onChange={(e) => setSyncSettings(prev => ({ ...prev, autoSync: e.target.checked }))}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">Enable automatic sync</span>
                </label>
                <p className="mt-1 text-xs text-gray-500 ml-6">
                  When enabled, reviews will be automatically synced based on your frequency settings
                </p>
              </div>
              
              <div>
                <label className="block text-sm text-gray-700 mb-1">Sync Frequency</label>
                <select
                  value={syncSettings.syncFrequency}
                  onChange={(e) => setSyncSettings(prev => ({ 
                    ...prev, 
                    syncFrequency: e.target.value as 'hourly' | 'daily' | 'weekly' 
                  }))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                >
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
              
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={syncSettings.notifyOnNewReviews}
                    onChange={(e) => setSyncSettings(prev => ({ ...prev, notifyOnNewReviews: e.target.checked }))}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">Notify me of new reviews</span>
                </label>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Response Settings</h4>
            <div className="space-y-4">
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={syncSettings.autoRespond}
                    onChange={(e) => setSyncSettings(prev => ({ ...prev, autoRespond: e.target.checked }))}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">Enable automatic responses</span>
                </label>
                <p className="mt-1 text-xs text-gray-500 ml-6">
                  When enabled, system will automatically respond to reviews based on templates
                </p>
              </div>
              
              <div>
                <label className="block text-sm text-gray-700 mb-1">Positive Review Template</label>
                <textarea
                  value={syncSettings.responseTemplates.positive}
                  onChange={(e) => setSyncSettings(prev => ({ 
                    ...prev, 
                    responseTemplates: {
                      ...prev.responseTemplates,
                      positive: e.target.value
                    }
                  }))}
                  rows={2}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                ></textarea>
              </div>
              
              <div>
                <label className="block text-sm text-gray-700 mb-1">Negative Review Template</label>
                <textarea
                  value={syncSettings.responseTemplates.negative}
                  onChange={(e) => setSyncSettings(prev => ({ 
                    ...prev, 
                    responseTemplates: {
                      ...prev.responseTemplates,
                      negative: e.target.value
                    }
                  }))}
                  rows={2}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                ></textarea>
              </div>
            </div>
          </div>
        </div>
        
        {/* Privacy Settings */}
        <div className="mt-6 bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" />
            Privacy Settings
          </h4>
          <div className="space-y-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={syncSettings.privacySettings.anonymizeCustomerData}
                onChange={(e) => setSyncSettings(prev => ({ 
                  ...prev, 
                  privacySettings: {
                    ...prev.privacySettings,
                    anonymizeCustomerData: e.target.checked
                  }
                }))}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm text-gray-600">Anonymize customer data in reports</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={syncSettings.privacySettings.storeReviewsLocally}
                onChange={(e) => setSyncSettings(prev => ({ 
                  ...prev, 
                  privacySettings: {
                    ...prev.privacySettings,
                    storeReviewsLocally: e.target.checked
                  }
                }))}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm text-gray-600">Store reviews in your database</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={syncSettings.privacySettings.limitDataRetention}
                onChange={(e) => setSyncSettings(prev => ({ 
                  ...prev, 
                  privacySettings: {
                    ...prev.privacySettings,
                    limitDataRetention: e.target.checked
                  }
                }))}
                className="rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-sm text-gray-600">Limit data retention period</span>
            </label>
            
            {syncSettings.privacySettings.limitDataRetention && (
              <div className="ml-6 mt-2">
                <label className="block text-sm text-gray-700 mb-1">Retention Period (days)</label>
                <input
                  type="number"
                  min="30"
                  max="3650"
                  value={syncSettings.privacySettings.retentionPeriodDays}
                  onChange={(e) => setSyncSettings(prev => ({ 
                    ...prev, 
                    privacySettings: {
                      ...prev.privacySettings,
                      retentionPeriodDays: parseInt(e.target.value)
                    }
                  }))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Configuration Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                {providers.find(p => p.id === showConfigModal)?.icon}
                <span>{providers.find(p => p.id === showConfigModal)?.name} Configuration</span>
              </h3>
              <button
                onClick={closeConfigModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Account Information */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Account Information</h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-4">
                    {providers.find(p => p.id === showConfigModal)?.icon}
                    <span className="ml-2 font-medium">
                      {providers.find(p => p.id === showConfigModal)?.accountName}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-gray-500">Reviews</div>
                      <div className="font-medium">{providers.find(p => p.id === showConfigModal)?.reviewCount}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Average Rating</div>
                      <div className="flex items-center">
                        {renderStars(providers.find(p => p.id === showConfigModal)?.averageRating || 0)}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500">Last Synced</div>
                      <div className="font-medium">
                        {providers.find(p => p.id === showConfigModal)?.lastSync?.toLocaleString() || 'Never'}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500">Status</div>
                      <div className="font-medium capitalize">
                        {providers.find(p => p.id === showConfigModal)?.status}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Platform-Specific Settings */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Platform Settings</h4>
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={true}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="ml-2 text-sm text-gray-700">Sync reviews automatically</span>
                    </label>
                  </div>
                  
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={true}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="ml-2 text-sm text-gray-700">Notify on new reviews</span>
                    </label>
                  </div>
                  
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={false}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="ml-2 text-sm text-gray-700">Auto-respond to reviews</span>
                    </label>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Minimum Rating to Display</label>
                    <select
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                      defaultValue="3"
                    >
                      <option value="1">1+ Stars</option>
                      <option value="2">2+ Stars</option>
                      <option value="3">3+ Stars</option>
                      <option value="4">4+ Stars</option>
                      <option value="5">5 Stars Only</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Notification Settings */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Notification Settings</h4>
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={true}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="ml-2 text-sm text-gray-700">Email notifications</span>
                    </label>
                  </div>
                  
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={false}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="ml-2 text-sm text-gray-700">SMS notifications</span>
                    </label>
                  </div>
                  
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={true}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="ml-2 text-sm text-gray-700">In-app notifications</span>
                    </label>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Notification Threshold</label>
                    <select
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                      defaultValue="all"
                    >
                      <option value="all">All reviews</option>
                      <option value="negative">Negative reviews only (1-3 stars)</option>
                      <option value="positive">Positive reviews only (4-5 stars)</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={closeConfigModal}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    toast.success('Configuration saved');
                    closeConfigModal();
                  }}
                >
                  Save Configuration
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Review Sync Status */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <BarChart2 className="h-5 w-5 text-primary mr-2" />
          Sync Status
        </h3>
        
        <div className="space-y-4">
          {providers.map((provider) => (
            <div key={provider.id} className="flex items-center justify-between border-b pb-3">
              <div className="flex items-center">
                {provider.icon}
                <span className="ml-2 font-medium">{provider.name}</span>
              </div>
              <div className="flex items-center">
                {provider.connected ? (
                  <>
                    <Check className="h-5 w-5 text-green-500 mr-2" />
                    <span className="text-sm text-gray-600">
                      Last sync: {provider.lastSync ? provider.lastSync.toLocaleTimeString() : 'Never'}
                    </span>
                  </>
                ) : (
                  <>
                    <X className="h-5 w-5 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-500">Not connected</span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-4 pt-4 border-t">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-700">Next Scheduled Sync</h4>
              <p className="text-sm text-gray-500">
                {syncSettings.syncFrequency === 'hourly' 
                  ? `Next sync at ${new Date(Math.ceil(Date.now() / 3600000) * 3600000).toLocaleTimeString()}`
                  : syncSettings.syncFrequency === 'daily'
                  ? `Next sync at midnight`
                  : `Next sync on ${new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString()}`}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleSyncAll}
              disabled={isSyncing !== null || !providers.some(p => p.connected)}
              className="flex items-center"
            >
              {isSyncing === 'all' ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Sync Now
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Review Integration Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Star className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-blue-800">About Review Integration</h4>
            <p className="mt-1 text-sm text-blue-700">
              The review integration system allows you to automatically sync customer reviews from multiple platforms.
              This helps you monitor your online reputation, respond to reviews promptly, and showcase positive
              testimonials on your website. All data is securely stored and handled in compliance with privacy regulations.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewPlatformIntegration;